import React, { useCallback, useEffect, useMemo } from "react"
import { get, isNil } from "lodash"
import { useSearchParams } from "react-router-dom"

import { GTM_AMENITY_SEARCH } from "constant"
import { pushDataLayer } from "utils/googletagmanager"

import { useTheme, Box } from "@mui/material"
import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"
import { useGetMapViewFromUrl } from "hooks/map"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { useFeatureCategory } from "hooks/feature"
import { useGeoLocation } from "providers/geolocation"
import { useMap } from "hooks/map/useMap"
import {
  Feature,
  useHighLightFeaturesMap,
} from "hooks/feature/useHighLightFeaturesMap"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { OrdinalSelector } from "components/OrdinalSelector"
import { OrdinalSelectMenu } from "components/OrdinalSelector/OrdinalSelectMenu"
import { VenueSelector } from "components/VenueSelector"
import { FeatureChipToggleBar } from "components/domains/features/ChipToggleBar"
import { LanguageButton } from "components/Language"
import { SearchToggleButton } from "components/domains/search"
import { OrdinalSelectSplide } from "components/OrdinalSelector/OrdinalSelectSplide"
import { LanguageSelectMenu } from "components/Language/LanguageSelectMenu"

export const FeatureByCategoryKioskVPage = ({
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { locationVenue, locationOrdinal } = useGeoLocation()
  const locationVenueId = get(locationVenue, "id")
  const [searchParams, setSearchParams] = useSearchParams()
  const { isMultiOrdinalVenue, dataLoaded, isMultiVenueProject, venues } =
    useVenue()
  const { openSearch } = useAppUI()
  const { t } = useTranslation()
  const { onChangeVenue } = useMap()

  const {
    viewingCategory,
    viewingVenueId,
    viewingOrdinal,
    comingFromOrdinal,
    featureCategories,
    searchableFeatures,
    quickSearchVenueLevels,
    handleClickQuickSearch,
  } = useFeatureCategory({
    initialVenueId: locationVenueId,
    initialOrdinal: locationOrdinal,
  })

  const { mapView } = useGetMapViewFromUrl()

  const { flyTo } = useIndoorMap()

  useEffect(() => {
    flyTo(mapView)
  }, [flyTo, mapView])

  const theme = useTheme()

  const enableMap =
    !isNil(viewingOrdinal) && isNil(comingFromOrdinal) && dataLoaded

  const { styleOverrides = {} } = theme.components[`AppMap`] || {}
  const {
    toolbarContainer,
    quickLinkContainer,
    ordinalSelectMenu,
    venueSelector,
    ordinalSelector,
    ordinalSelectSplide,
    navigationWrapper,
    actionContainer,
    languageSelectMenu,
    actionButton,
    searchBarContainer,
  } = styleOverrides

  const onClickOrdinal = useCallback(
    (ordinal) => {
      const currentParams = Object.fromEntries(searchParams.entries())
      setSearchParams({ ...currentParams, ordinal })
    },
    [setSearchParams, searchParams]
  )

  const parsedOrdinal = useMemo(() => Number(viewingOrdinal), [viewingOrdinal])

  useHighLightFeaturesMap(searchableFeatures as Feature[], parsedOrdinal, {
    enabled: enableMap,
  })

  useEffect(() => {
    if (viewingCategory) {
      pushDataLayer({
        event: GTM_AMENITY_SEARCH,
        amenity_category: viewingCategory,
      })
    }
  }, [viewingCategory])

  return (
    <>
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelector}>
          <OrdinalSelector
            selectedOrdinal={Number(viewingOrdinal)}
            levels={quickSearchVenueLevels}
            onClickOrdinal={onClickOrdinal}
            locationOrdinal={locationOrdinal}
            currentVenue={locationVenueId}
          />
        </Box>
      )}
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectMenu}>
          <OrdinalSelectMenu
            selectedOrdinal={Number(viewingOrdinal)}
            levels={quickSearchVenueLevels}
            onChangeOrdinal={onClickOrdinal}
          />
        </Box>
      )}
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectSplide}>
          <OrdinalSelectSplide
            key={`${viewingCategory}-${viewingOrdinal}`} //force remount when category or ordinal changes
            selectedOrdinal={Number(viewingOrdinal)}
            levels={quickSearchVenueLevels}
            onChangeOrdinal={onClickOrdinal}
          />
        </Box>
      )}

      {isMultiVenueProject && (
        <Box sx={venueSelector}>
          <VenueSelector
            selectedVenue={viewingVenueId}
            venues={venues}
            onClickVenue={onChangeVenue}
            currentVenue={locationVenueId}
          />
        </Box>
      )}
      <Box key="app-navigation" sx={navigationWrapper}>
        <Box sx={quickLinkContainer}>
          <FeatureChipToggleBar
            categories={featureCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
        <Box sx={toolbarContainer}>
          <Box sx={actionContainer}>
            <LanguageSelectMenu sx={languageSelectMenu} />
            <LanguageButton variant="contained" sx={actionButton} />
            {/* //TODO:: Add a wheel chair's mode handler function later */}
            {/*  <Button
            variant="contained"
            sx={actionButton}
            onClick={() => {}}
            startIcon={<RestroomWheelchairIcon />}
          /> */}
            {additionalMenu}
            {/* //TODO:: Create Toolbar component and refactor Code */}
          </Box>
          <Box sx={searchBarContainer}>
            <SearchToggleButton
              placeholder={t("Search shops and more...")}
              onClick={openSearch}
            />
          </Box>
        </Box>
        {AppNavigation && <AppNavigation />}
      </Box>
    </>
  )
}
